/**
 * Catalogue plat des pages / actions pour la palette de recherche globale.
 * Les `keywords` enrichissent le matching (FR / alias métier).
 */
export interface NavPageEntry {
  id: string;
  title: string;
  subtitle?: string;
  route: string;
  icon: string;
  keywords?: string[];
  roles?: string[];
  kind?: 'page' | 'action';
}

export const NAV_PAGE_ENTRIES: NavPageEntry[] = [
  { id: 'home', title: 'Accueil', route: '/Accueil', icon: 'space_dashboard', keywords: ['dashboard', 'home'] },
  { id: 'inbox', title: 'Inbox unifiée', route: '/inbox', icon: 'forum', keywords: ['messages', 'messenger', 'whatsapp'] },
  { id: 'support', title: 'Centre de support', route: '/support', icon: 'support_agent', keywords: ['tickets', 'helpdesk'] },
  { id: 'docs', title: 'Centre d\'aide', route: '/docs', icon: 'menu_book', keywords: ['aide', 'documentation', 'guides'] },
  { id: 'pos', title: 'Point de vente', route: '/pos', icon: 'point_of_sale', keywords: ['caisse', 'pos', 'vente'] },
  { id: 'products', title: 'Catalogue produits', route: '/produits', icon: 'inventory_2', keywords: ['produits', 'catalog', 'sku'] },
  { id: 'orders', title: 'Commandes', route: '/commande', icon: 'shopping_cart', keywords: ['orders', 'ventes'] },
  { id: 'clients', title: 'Clients', route: '/clients', icon: 'people', keywords: ['customers', 'contacts'] },
  { id: 'stores', title: 'Boutiques', route: '/Boutique', icon: 'storefront', keywords: ['stores', 'magasins'] },
  { id: 'kimbyala', title: 'Kimbyala', route: '/kimbyala', icon: 'event_note', keywords: ['kmb', 'billet', 'échéance', 'effet', 'كميالة'], roles: ['vendor', 'admin'] },
  { id: 'accounting', title: 'Comptabilité', route: '/comptabilite', icon: 'account_balance', keywords: ['finance', 'compta'], roles: ['vendor', 'admin'] },
  { id: 'cashbox', title: 'Caisse / trésorerie', route: '/Gestionnaire', icon: 'account_balance_wallet', keywords: ['cash', 'dépenses'] },
  { id: 'telephonie', title: 'Téléphonie', route: '/telephonie', icon: 'call', keywords: ['callcenter', 'softphone', 'goip'], roles: ['vendor', 'admin'] },
  { id: 'inventory', title: 'Inventaire', route: '/inventaire', icon: 'warehouse', keywords: ['stock', 'entrepôt'] },
  { id: 'stock', title: 'Stock produits', route: '/inventaire/stock', icon: 'inventory_2', keywords: ['stock'] },
  { id: 'moves', title: 'Mouvements de stock', route: '/inventaire/mouvements', icon: 'receipt_long', keywords: ['bons'] },
  { id: 'shipments', title: 'Expéditions', route: '/expeditions', icon: 'local_shipping', keywords: ['livraison', 'shipping'] },
  { id: 'marketing', title: 'Marketing', route: '/marketing', icon: 'campaign', keywords: ['ads', 'meta', 'facebook'] },
  { id: 'prospection', title: 'Prospection CRM', route: '/marketing/prospection', icon: 'travel_explore', keywords: ['crm', 'leads', 'prospects'] },
  { id: 'atelier', title: 'Atelier créatif', route: '/marketing/atelier', icon: 'auto_awesome', keywords: ['studio', 'visuels'] },
  { id: 'sites', title: 'Mes sites', route: '/sites', icon: 'language', keywords: ['website', 'landing'], roles: ['vendor', 'admin'] },
  { id: 'ai-team', title: 'Équipe IA', route: '/ai-agents', icon: 'smart_toy', keywords: ['agents', 'ia'], roles: ['vendor', 'admin'] },
  { id: 'right-hand', title: 'Bras droit IA', route: '/ai-agents/right-hand', icon: 'support_agent', keywords: ['rh', 'assistant'], roles: ['vendor', 'admin'] },
  { id: 'tasks', title: 'Tableau des tâches', route: '/tasks/board', icon: 'view_kanban', keywords: ['kanban', 'todo'] },
  { id: 'agents', title: 'Équipe terrain', route: '/agents', icon: 'badge', keywords: ['livreurs', 'staff'] },
  { id: 'employees', title: 'Employés', route: '/Employe', icon: 'badge', keywords: ['rh', 'salariés'] },
  { id: 'settings', title: 'Paramètres', route: '/settings', icon: 'settings', keywords: ['config'] },
  { id: 'profile', title: 'Mon profil', route: '/profile', icon: 'account_circle', keywords: ['compte'] },
  { id: 'scan', title: 'Scan hub', route: '/scan', icon: 'qr_code_scanner', keywords: ['qr', 'barcode'] },
  { id: 'labels', title: 'Étiquettes', route: '/etiquettes', icon: 'print', keywords: ['print', 'labels'] },
  { id: 'dropship', title: 'Dropshipping', route: '/dropship/dashboard', icon: 'handshake', keywords: ['fournisseur'], roles: ['vendor', 'admin'] },
  { id: 'supervision', title: 'Supervision', route: '/admin/supervision', icon: 'shield_moon', keywords: ['audit', 'admin'], roles: ['admin'] },

  // Actions rapides
  {
    id: 'act-new-order',
    title: 'Nouvelle commande',
    subtitle: 'Créer une commande',
    route: '/commande',
    icon: 'add_shopping_cart',
    kind: 'action',
    keywords: ['créer', 'ajouter', 'new order'],
  },
  {
    id: 'act-new-kimbyala',
    title: 'Nouvel échéancier Kimbyala',
    subtitle: 'Billets à ordre',
    route: '/kimbyala?new=1',
    icon: 'event_note',
    kind: 'action',
    roles: ['vendor', 'admin'],
    keywords: ['créer kimbyala', 'billet'],
  },
  {
    id: 'act-new-product',
    title: 'Nouveau produit',
    route: '/produits',
    icon: 'add_box',
    kind: 'action',
    keywords: ['créer produit', 'ajouter'],
  },
  {
    id: 'act-pos',
    title: 'Ouvrir la caisse POS',
    route: '/pos',
    icon: 'point_of_sale',
    kind: 'action',
    keywords: ['encaisser', 'vendre'],
  },
];

export function filterNavPages(
  q: string,
  hasRole: (r: string) => boolean,
): NavPageEntry[] {
  const needle = (q || '').trim().toLowerCase();
  return NAV_PAGE_ENTRIES.filter((e) => {
    if (e.roles && !e.roles.some((r) => hasRole(r))) return false;
    if (!needle) return e.kind !== 'action'; // pages seules à vide
    const hay = [e.title, e.subtitle, e.route, ...(e.keywords || [])]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(needle);
  }).slice(0, needle ? 12 : 8);
}
